/**
 * usePractitionerConsent — Data hook for the Practitioner Consent screen.
 *
 * Fetches the client's consent records (which practitioners have access),
 * supports toggling individual consent fields, and revoking practitioners.
 * Uses optimistic updates with rollback on failure.
 *
 * Crisis fields persist to allow_crisis_notifications,
 * crisis_notification_urgency_preference, crisis_notification_contact_method.
 *
 * Wire shape is owned by `PractitionerConsentListDto` in
 * `@dramadetector/shared/schemas`. The hook validates the server response
 * via `fetchJsonValidated` and converts the snake_case DTO into the
 * camelCase `PractitionerConsentRecord` shape used by the UI.
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchJsonValidated,
} from '@dramadetector/api-client';
import {
  PractitionerConsentListDto,
  type PractitionerConsentRecord as PractitionerConsentRecordDtoShape,
} from '@dramadetector/shared/schemas';
import { getEdgeFunctionUrl } from '../lib/api-url';
import { getInitials } from './practitioner-utils';

// ─── Types ───────────────────────────────────────────────────────────────────

export type ConsentField =
  | 'canViewHistory' | 'canViewOriginalMessage' | 'canChat'
  | 'requiresApprovalForResults' | 'allowCrisisNotifications'
  | 'crisisUrgencyLevel' | 'crisisContactMethod';

export interface PractitionerConsentRecord {
  id: string; practitionerId: string; practitionerName: string;
  practitionerRole: string; practitionerInitials: string;
  gradientIndex: number; status: 'active' | 'pending'; invitedAt: string;
  consent: {
    canViewHistory: boolean; canViewOriginalMessage: boolean;
    canChat: boolean; requiresApprovalForResults: boolean;
    allowCrisisNotifications: boolean;
    crisisUrgencyLevel: string;
    crisisContactMethod: string;
  };
}

export interface UsePractitionerConsentReturn {
  records: PractitionerConsentRecord[];
  isLoading: boolean; error: Error | null;
  updateConsent: (practitionerId: string, field: ConsentField, value: boolean) => Promise<void>;
  revokePractitioner: (practitionerId: string) => Promise<void>;
  refetch: () => Promise<void>;
}

// ─── Constants ───────────────────────────────────────────────────────────────

export const practitionerConsentKeys = {
  all: ['practitioner-consent'] as const,
  user: (userId: string | null) => [...practitionerConsentKeys.all, 'user', userId] as const,
};

// ─── Mappers ─────────────────────────────────────────────────────────────────

/** snake_case DTO row → camelCase UI record. */
function dtoToRecord(
  row: PractitionerConsentRecordDtoShape,
  idx: number,
): PractitionerConsentRecord {
  const name = row.practice_name ?? 'Practitioner';
  const role = row.practice_type
    ? row.practice_type.charAt(0).toUpperCase() + row.practice_type.slice(1)
    : 'Practitioner';
  return {
    id: row.id,
    practitionerId: row.practitioner_id,
    practitionerName: name,
    practitionerRole: role,
    practitionerInitials: getInitials(name),
    gradientIndex: idx % 6,
    status: 'active' as const,
    invitedAt: row.created_at ?? '',
    consent: {
      canViewHistory: row.can_view_history,
      canViewOriginalMessage: row.can_view_original_message,
      canChat: row.can_chat,
      requiresApprovalForResults: row.requires_approval_for_results,
      allowCrisisNotifications: row.allow_crisis_notifications,
      crisisUrgencyLevel: row.crisis_notification_urgency_preference ?? '',
      crisisContactMethod: row.crisis_notification_contact_method ?? '',
    },
  };
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function usePractitionerConsent(): UsePractitionerConsentReturn {
  const { user, accessToken } = useAuth();
  const userId = user?.id ?? null;
  const queryClient = useQueryClient();
  const queryKey = practitionerConsentKeys.user(userId);

  const { data: records = [], isLoading, error, refetch: queryRefetch } = useQuery({
    queryKey,
    queryFn: async (): Promise<PractitionerConsentRecord[]> => {
      if (!userId) return [];

      const dtoList = await fetchJsonValidated(
        PractitionerConsentListDto,
        getEdgeFunctionUrl('get-practitioner-consent'),
      );
      return dtoList.map((row, idx) => dtoToRecord(row, idx));
    },
    staleTime: 1000 * 30, gcTime: 1000 * 60 * 15,
    enabled: !!accessToken && !!userId, refetchOnMount: true,
  });

  // ─── Update mutation with optimistic rollback ──────────────────────────

  const updateMutation = useMutation({
    mutationFn: async (_args: {
      practitionerId: string; field: ConsentField; value: boolean;
    }) => {
      if (!userId) throw new Error('Not authenticated');
      // NocoDB #3760: this previously POSTed to `/get-practitioner-consent`
      // — a GET-only route. The handler 405'd; the optimistic update +
      // onSettled invalidate masked the failure as a brief flash. Now
      // throws explicitly so the rollback path runs and the UI surfaces
      // an error state. A dedicated `update-practitioner-consent`
      // handler is needed to make this functional again — see #3760.
      throw new Error(
        'Updating practitioner consent is not yet supported on this build. ' +
          'The dedicated update handler is tracked in NocoDB #3760.',
      );
    },
    onMutate: async ({ practitionerId, field, value }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PractitionerConsentRecord[]>(queryKey);
      queryClient.setQueryData<PractitionerConsentRecord[]>(queryKey, (old = []) =>
        old.map((r) => {
          if (r.practitionerId !== practitionerId) return r;
          if (field === 'allowCrisisNotifications' && !value) {
            return { ...r, consent: { ...r.consent, allowCrisisNotifications: false, crisisUrgencyLevel: '', crisisContactMethod: '' } };
          }
          return { ...r, consent: { ...r.consent, [field]: value } };
        })
      );
      return { previous };
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous); },
    onSettled: () => { queryClient.invalidateQueries({ queryKey }); },
  });

  // ─── Revoke mutation with optimistic removal ──────────────────────────

  const revokeMutation = useMutation({
    mutationFn: async (_args: { practitionerId: string }) => {
      if (!userId) throw new Error('Not authenticated');
      // NocoDB #3760: same as updateMutation — POST'd to a GET-only
      // route, throws explicitly now so rollback runs and the UI shows
      // an error instead of the previous silent failure.
      throw new Error(
        'Revoking practitioner consent is not yet supported on this build. ' +
          'The dedicated revoke handler is tracked in NocoDB #3760.',
      );
    },
    onMutate: async ({ practitionerId }) => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<PractitionerConsentRecord[]>(queryKey);
      queryClient.setQueryData<PractitionerConsentRecord[]>(queryKey,
        (old = []) => old.filter((r) => r.practitionerId !== practitionerId));
      return { previous };
    },
    onError: (_e, _v, ctx) => { if (ctx?.previous) queryClient.setQueryData(queryKey, ctx.previous); },
    onSettled: () => { queryClient.invalidateQueries({ queryKey }); },
  });

  const updateConsent = useCallback(
    (pId: string, field: ConsentField, value: boolean) =>
      updateMutation.mutateAsync({ practitionerId: pId, field, value }),
    [updateMutation]
  );

  const revokePractitioner = useCallback(
    (pId: string) =>
      revokeMutation.mutateAsync({ practitionerId: pId }),
    [revokeMutation]
  );

  const refetch = useCallback(async () => { await queryRefetch(); }, [queryRefetch]);

  return {
    records, isLoading: isLoading && !!accessToken,
    error: error as Error | null, updateConsent, revokePractitioner, refetch,
  };
}
